import { ListenerEffectAPI } from '@reduxjs/toolkit';

import { AppDispatch, RootState } from 'src/store';

export type FlowPageLoader = (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
) => Promise<void>;

const pageLoaders: Record<string, Record<string, FlowPageLoader>> = {};

/**
 * `addPageLoader` is a way to have a page load data, change state, do side
 * effects before the page is displayed
 *
 * @param {string} slug - the page.slug the loader will run on
 * @param {string} key - a unique identifier for the loader, this is
 *   needed because during hot-module-reloading pageLoaders will be reloaded
 *   but our `pageLoaders` object will still have the old loaders in it. This
 *   allows us to clear those out. If you call addPageLoader with the same key
 *   twice there will be a console.info logged out, you should not see this on
 *   page load, but during hmr this info message is expected.
 * @param {FlowPageLoader} loader - the function to run before the page is
 *   displayed
 */
export const addPageLoader = (slug: string, key: string, loader: FlowPageLoader) => {
  if (pageLoaders[slug]) {
    if (pageLoaders[slug][key]) {
      // This happens during hot-module reloading, it should not happen on page
      // refresh, so if you see this after a page refresh something is broken
      //
      // eslint-disable-next-line no-console
      console.info('Replacing loader for', slug, key);
    }
    pageLoaders[slug][key] = loader;
  } else {
    pageLoaders[slug] = { [key]: loader };
  }
};

export const getPageLoader = (slug: string): Array<FlowPageLoader> | undefined => {
  return pageLoaders[slug]
    ? Object.entries(pageLoaders[slug]).map(([_, loader]) => loader)
    : undefined;
};

export const getPageLoaderSlugs = () => {
  return Object.keys(pageLoaders);
};
